import { motion } from 'motion/react';
import HeaderTitle from './header-title/header-title';
import RiskGauge from './risk-gauge/risk-gauge';
import RiskStatusBadge from './risk-status-badge/risk-status-badge';
import { BenchmarkCategory } from '@/api/generated-catch-backend/model';

interface DashboardHeaderCardProps {
  name?: string;
  benchmark: BenchmarkCategory;
  currentRisk: number;
  averageRisk: number;
  methodologyUrl?: string;
  userGender?: 'male' | 'female' | null;
  onBadgeClick: () => void;
  /** Content to render below the card (e.g., tabs) */
  belowCardContent?: React.ReactNode;
}

export default function DashboardHeaderCard({
  name,
  benchmark,
  currentRisk,
  averageRisk,
  methodologyUrl,
  userGender,
  onBadgeClick,
  belowCardContent,
}: DashboardHeaderCardProps) {
  return (
    <div className="relative z-10 mx-auto max-w-7xl px-4 pt-14 pb-8 sm:px-6 md:pt-16 md:pb-12 lg:px-8">
      {/* Card container */}
      <motion.div
        className="rounded-2xl bg-[rgba(255,255,255,0.08)] p-6 backdrop-blur-sm md:p-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
      >
        <div className="flex min-h-[250px] flex-col lg:flex-row lg:gap-12">
          {/* Left side content */}
          <div className="order-2 flex flex-shrink-0 flex-col items-center gap-6 lg:order-1">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <RiskGauge riskPercentage={currentRisk} populationAverage={averageRisk} />
            </motion.div>

            <RiskStatusBadge riskLevel={benchmark} populationAverage={averageRisk} onBadgeClick={onBadgeClick} />
            {/* Link below odometer on mobile only */}
            <motion.div className="lg:hidden" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 0.6 }}>
              <a
                href={methodologyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="focus:ring-opacity-50 cursor-pointer rounded-sm text-[16px] leading-[1.3] font-semibold text-[#7cf1c7] underline transition-opacity hover:opacity-80 focus:ring-2 focus:ring-[#7cf1c7] focus:outline-none"
                aria-label="Learn how your cancer risk is calculated (opens in new tab)"
              >
                Learn how your risk is calculated
              </a>
            </motion.div>
          </div>

          {/* Right side content - fills remaining space */}
          <div className="order-1 flex flex-1 flex-col justify-center lg:order-2">
            <HeaderTitle
              name={name}
              benchmark={benchmark}
              methodologyUrl={methodologyUrl}
              lifetimeRisk={currentRisk}
              populationAverage={averageRisk}
              userGender={userGender}
              className="flex h-full flex-col justify-center"
            />
          </div>
        </div>
      </motion.div>

      {/* Content below the card */}
      {belowCardContent && (
        <div className="mt-6 w-[335px] mx-auto">
          {belowCardContent}
        </div>
      )}
    </div>
  );
}
